// components/strategy/StrategyAccountsPanel.jsx — Phase 3B account
// management for Super Admins: create (with optional e-mail invite),
// suspend/reactivate, assign role, revoke an unused invitation.
// Server enforces super_admin-only + same-country on every one of
// these calls — this panel has no client-side permission bypass to
// worry about, it just surfaces what the API already allows/rejects.
import React, { useState } from "react";
import { strategyEndpoints } from "../../lib/strategyApi";

export const StrategyAccountsPanel = ({ countryCode }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [targetId, setTargetId] = useState("");
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState("");

  const createAccount = async (e) => {
    e.preventDefault();
    setStatus("");
    try {
      const { data } = await strategyEndpoints.createAccount(email.trim(), countryCode, role);
      setLastCreated(data);
      setEmail("");
      setStatus(`Compte créé : ${data.account.id}`);
    } catch (e) {
      setStatus(e?.response?.data?.detail || "Échec de la création");
    }
  };

  const act = async (fn, okMsg) => {
    setStatus("");
    try {
      await fn();
      setStatus(okMsg);
    } catch (e) {
      setStatus(e?.response?.data?.detail || "Échec de l'opération");
    }
  };

  return (
    <div className="space-y-3" data-testid="strategy-accounts-panel">
      <form onSubmit={createAccount} className="border-2 border-rescue-line p-3 space-y-2">
        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-rescue-mute">Inviter un compte</div>
        <input data-testid="strategy-new-account-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="email@test.invalid" className="w-full border-2 border-rescue-line px-2 py-1.5 text-xs" />
        <select data-testid="strategy-new-account-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full border-2 border-rescue-line px-2 py-1.5 text-xs">
          <option value="admin">Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
        <label className="flex items-center gap-2 text-[11px]">
          <input type="checkbox" data-testid="strategy-new-account-notify" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} />
          Envoyer le lien par e-mail (adresse de test uniquement)
        </label>
        <button type="submit" data-testid="strategy-create-account-btn" className="w-full bg-rescue-ink text-white py-2 text-xs font-bold uppercase">
          Créer
        </button>
        {lastCreated?.activation_token && (
          <div className="text-[10px] font-mono break-all bg-rescue-bg p-2" data-testid="strategy-activation-token-display">
            Jeton (dev) : {lastCreated.activation_token}
          </div>
        )}
      </form>

      <div className="border-2 border-rescue-line p-3 space-y-2">
        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-rescue-mute">Gérer un compte existant</div>
        <input data-testid="strategy-target-account-id" value={targetId} onChange={(e) => setTargetId(e.target.value)}
          placeholder="ID du compte" className="w-full border-2 border-rescue-line px-2 py-1.5 text-xs font-mono" />
        <input data-testid="strategy-suspend-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Motif (suspension)" className="w-full border-2 border-rescue-line px-2 py-1.5 text-xs" />
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="strategy-suspend-btn" onClick={() => act(() => strategyEndpoints.suspendAccount(targetId, countryCode, reason), "Compte suspendu")}
            className="bg-rescue-red text-white py-1.5 text-[11px] font-bold uppercase">Suspendre</button>
          <button data-testid="strategy-reactivate-btn" onClick={() => act(() => strategyEndpoints.reactivateAccount(targetId, countryCode), "Compte réactivé")}
            className="bg-rescue-ink text-white py-1.5 text-[11px] font-bold uppercase">Réactiver</button>
          <button data-testid="strategy-assign-admin-btn" onClick={() => act(() => strategyEndpoints.assignRole(targetId, countryCode, "admin"), "Rôle: admin")}
            className="border-2 border-rescue-ink py-1.5 text-[11px] font-bold uppercase">Rôle → Admin</button>
          <button data-testid="strategy-assign-super-admin-btn" onClick={() => act(() => strategyEndpoints.assignRole(targetId, countryCode, "super_admin"), "Rôle: super_admin")}
            className="border-2 border-rescue-ink py-1.5 text-[11px] font-bold uppercase">Rôle → Super Admin</button>
          <button data-testid="strategy-revoke-invitation-btn" onClick={() => act(() => strategyEndpoints.revokeInvitation(targetId, countryCode), "Invitation révoquée")}
            className="col-span-2 border-2 border-rescue-red text-rescue-red py-1.5 text-[11px] font-bold uppercase">Révoquer l'invitation</button>
        </div>
      </div>

      {status && <div className="text-xs font-bold" data-testid="strategy-accounts-status">{status}</div>}
    </div>
  );
};
